import Circular from "@/components/Circular";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import CircularProgress from "@/components/CircularProgress";
import ConditionalTooltip from "@/components/ConditionalTooltip";
import { iconSizes, strokeWidth } from "@/utils/iconProps";
import { getProgressPillProps } from "@/utils/progressPill";
import { themeColors } from "@/utils/themeColors";
import { StudySessionProgressEnum } from "@knowt/syncing/graphql/schema";
import { FlashcardsMastery } from "@knowt/syncing/hooks/study/statsUtils";
import Skeleton from "@mui/material/Skeleton";
import { ArrowRightIcon } from "lucide-react";

export const studyProgressTooltips: { [key in StudySessionProgressEnum]: string } = {
    [StudySessionProgressEnum.NEW]: "Flashcards you haven't started studying yet",
    [StudySessionProgressEnum.LEARNING]: "Flashcards you still need to practice",
    [StudySessionProgressEnum.REVIEWING]: "Flashcards you've almost mastered",
    [StudySessionProgressEnum.MASTERED]: "Flashcards you're a pro at",
};

const ProgressPill = ({ progress, style }: { progress?: StudySessionProgressEnum; style?: React.CSSProperties }) => {
    if (!progress)
        return (
            <Skeleton
                animation="wave"
                variant="rounded"
                width={"16rem"}
                height={"4rem"}
                style={{
                    borderRadius: 999,
                }}
            />
        );

    const { backgroundColor, label, color, progressBg, progressValue } = getProgressPillProps(progress);

    return (
        <ConditionalTooltip tooltip={studyProgressTooltips[progress]}>
            <Circular
                radius="auto"
                style={{
                    width: "16rem",
                    minWidth: "16rem",
                    height: "4rem",
                    color,
                    backgroundColor,
                    gap: "0.8rem",
                    justifyContent: "flex-start",
                    padding: "0 0 0 1.5rem",
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    ...style,
                }}>
                <CircularProgress
                    value={progressValue}
                    bgColor={progressBg}
                    color={color}
                    size={"2rem"}
                    thickness={8}
                    title={studyProgressTooltips[progress]}
                />
                {label}
            </Circular>
        </ConditionalTooltip>
    );
};

export default ProgressPill;

export const MasteryPill = ({
    mastery,
    onClick,
}: { mastery: FlashcardsMastery["flashcardId"]; onClick?: () => void }) => {
    const masteryPercentage = Math.round(
        (mastery.MASTERED / (mastery.NEW + mastery.LEARNING + mastery.REVIEWING + mastery.MASTERED)) * 100
    );

    return (
        <CircularRectTextButton
            onClick={onClick}
            sx={{
                width: "18rem",
                height: "4rem",
                color: themeColors.neutralWhite,
                backgroundColor: themeColors.neutralBlack,
                gap: "0.8rem",
                justifyContent: "flex-start",
                padding: "0 0 0 1.5rem",
                fontSize: "1.5rem",
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": {
                    border: `1px solid ${themeColors.neutral1}`,
                },
            }}>
            <CircularProgress
                value={masteryPercentage}
                bgColor={themeColors.primaryLight}
                color={themeColors.primary}
                size={"2rem"}
                thickness={8}
                title={`${masteryPercentage}% mastered`}
            />
            {Number.isNaN(masteryPercentage) ? 0 : (masteryPercentage ?? 0)}% Mastered{" "}
            <ArrowRightIcon size={iconSizes.SM_S} strokeWidth={strokeWidth.bold} />
        </CircularRectTextButton>
    );
};
